// DRM Engine for handling FairPlay DRM
// Supports multiple keys per HLS file with proper session tracking

const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

class Logger {
  constructor(level = LogLevel.INFO) {
    this.level = level;
  }

  debug(...args) {
    if (this.level <= LogLevel.DEBUG) console.debug('[DRM]', ...args);
  }

  info(...args) {
    if (this.level <= LogLevel.INFO) console.info('[DRM]', ...args);
  }

  warn(...args) {
    if (this.level <= LogLevel.WARN) console.warn('[DRM]', ...args);
  }

  error(...args) {
    if (this.level <= LogLevel.ERROR) console.error('[DRM]', ...args);
  }
}

/**
 * Extract key ID from FairPlay initData
 * @param {ArrayBuffer} initData
 * @returns {string} Hex string of key ID
 */
function extractKeyId(initData) {
  // FairPlay initData is typically the skd:// URI as UTF-16
  // Convert to string and use as identifier
  const uint8 = new Uint8Array(initData);
  // Create a hash/identifier from the initData bytes
  let hash = 0;
  for (let i = 0; i < uint8.length; i++) {
    hash = ((hash << 5) - hash + uint8[i]) | 0;
  }
  return hash.toString(16);
}

export class StreamDrmEngine {
  constructor(fpLicenseUrl, fpCertificateUrl, logLevel = LogLevel.INFO) {
    this.fpLicenseUrl = fpLicenseUrl;
    this.fpCertificateUrl = fpCertificateUrl;
    this.logger_ = new Logger(logLevel);
    this.fpCertificate = null;

    // MediaKeys setup tracking
    this.mediaKeysPromise = null;
    this.mediaKeysReady = false;

    // Session tracking: Map<keyId, MediaKeySession>
    this.sessions = new Map();
    // Pending requests: Map<keyId, Promise<void>> - prevents duplicate processing
    this.pendingRequests = new Map();
    // Track which keys have become usable
    this.usableKeys = new Set();
    // Timeout for keys that never become usable
    this._keyUsableTimeout = null;

    // Bound event handler for cleanup
    this._boundOnEncrypted = null;
    this._videoElement = null;

    // Error callback for dispatching errors to the player
    this.onError = null;
  }

  /**
   * Load FairPlay certificate from server
   */
  async loadFpCertificate() {
    if (this.fpCertificate) {
      return;
    }
    try {
      this.logger_.debug(
        'Loading FairPlay certificate from:',
        this.fpCertificateUrl,
      );
      const response = await fetch(this.fpCertificateUrl);
      if (!response.ok) {
        throw new Error(`Certificate fetch failed: ${response.status}`);
      }
      this.fpCertificate = await response.arrayBuffer();
      this.logger_.debug(
        'FairPlay certificate loaded, size:',
        this.fpCertificate.byteLength,
      );
    } catch (e) {
      this.logger_.error(
        `Could not load FairPlay certificate at ${this.fpCertificateUrl}`,
        e,
      );
      throw e;
    }
  }

  /**
   * Create and configure MediaKeys for the video element
   * Uses a promise to ensure only one setup happens even with concurrent calls
   * @param {HTMLVideoElement} video
   * @returns {Promise<MediaKeys>}
   */
  async setupMediaKeys(video) {
    // Return existing promise if setup is in progress
    if (this.mediaKeysPromise) {
      return this.mediaKeysPromise;
    }

    // Return immediately if already set up
    if (this.mediaKeysReady && video.mediaKeys) {
      return video.mediaKeys;
    }

    this.mediaKeysPromise = this._createMediaKeys(video);

    try {
      const mediaKeys = await this.mediaKeysPromise;
      this.mediaKeysReady = true;
      return mediaKeys;
    } catch (e) {
      // Reset on failure to allow retry
      this.mediaKeysPromise = null;
      throw e;
    }
  }

  /**
   * Internal method to create MediaKeys
   * @param {HTMLVideoElement} video
   * @returns {Promise<MediaKeys>}
   */
  async _createMediaKeys(video) {
    this.logger_.debug('Requesting MediaKeySystemAccess for com.apple.fps');

    const access = await navigator.requestMediaKeySystemAccess(
      'com.apple.fps',
      [
        {
          initDataTypes: ['cenc', 'sinf', 'skd'],
          videoCapabilities: [
            { contentType: 'application/vnd.apple.mpegurl', robustness: '' },
          ],
          distinctiveIdentifier: 'not-allowed',
          persistentState: 'not-allowed',
          sessionTypes: ['temporary'],
        },
      ],
    );

    this.logger_.debug('Creating MediaKeys');
    const mediaKeys = await access.createMediaKeys();

    this.logger_.debug('Setting server certificate');
    await mediaKeys.setServerCertificate(this.fpCertificate);

    this.logger_.debug('Setting MediaKeys on video element');
    await video.setMediaKeys(mediaKeys);

    this.logger_.debug('FairPlay MediaKeys setup complete');
    return mediaKeys;
  }

  /**
   * Initialize FairPlay DRM - loads certificate and sets up encrypted event listener
   * @param {HTMLVideoElement} videoEl
   */
  async initFairPlay(videoEl) {
    try {
      // Clean up any previous initialization
      this.cleanup();

      await this.loadFpCertificate();

      // Store reference for cleanup
      this._videoElement = videoEl;
      this._boundOnEncrypted = (e) => this.onFpEncrypted(e);

      this.logger_.debug('Adding encrypted event listener');
      videoEl.addEventListener('encrypted', this._boundOnEncrypted);

      this.logger_.info('FairPlay DRM initialized');
    } catch (e) {
      this.logger_.error('Failed to initialize FairPlay DRM:', e);
      throw e;
    }
  }

  /**
   * Handle encrypted event from video element
   * Implements proper session tracking to handle multiple keys
   * @param {MediaEncryptedEvent} event
   */
  async onFpEncrypted(event) {
    const initDataType = event.initDataType;
    const initData = event.initData;

    // Skip invalid events
    if (!initDataType || !initData) {
      this.logger_.warn(
        'Skipping invalid encrypted event - missing initDataType or initData',
      );
      return;
    }

    const keyId = extractKeyId(initData);

    this.logger_.debug('Encrypted event received for key:', keyId);

    // Check if we already have a session for this key
    if (this.sessions.has(keyId)) {
      this.logger_.debug(`Session already exists for key ${keyId}, skipping`);
      return;
    }

    // Check if we're already processing this key (race condition prevention)
    if (this.pendingRequests.has(keyId)) {
      this.logger_.debug(
        `Request already pending for key ${keyId}, skipping duplicate`,
      );
      return;
    }

    // Process keys in parallel to avoid blocking when multiple keys are needed
    const processingPromise = this._processEncryptedEvent(
      event.target,
      initDataType,
      initData,
      keyId,
    );
    this.pendingRequests.set(keyId, processingPromise);

    try {
      await processingPromise;
    } catch (error) {
      this.logger_.error(`Processing failed for key ${keyId}:`, error);
    } finally {
      this.pendingRequests.delete(keyId);
    }
  }

  /**
   * Process an encrypted event and create a session
   * @param {HTMLVideoElement} video
   * @param {string} initDataType
   * @param {ArrayBuffer} initData
   * @param {string} keyId
   */
  async _processEncryptedEvent(video, initDataType, initData, keyId) {
    try {
      // Ensure MediaKeys are set up
      await this.setupMediaKeys(video);

      // Create session
      this.logger_.debug(`Creating media key session for key ${keyId}`);
      const session = video.mediaKeys.createSession();

      // Store session immediately to prevent duplicates
      this.sessions.set(keyId, session);

      // Listen for session events (for monitoring, not blocking)
      session.addEventListener('keystatuseschange', () => {
        this._onKeyStatusChange(session, keyId);
      });

      // Generate license request
      this.logger_.debug(
        `Generating request for key ${keyId} with initDataType:`,
        initDataType,
      );
      await session.generateRequest(initDataType, initData);

      // Wait for license request message
      const message = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout waiting for license request message'));
        }, 30000);

        session.addEventListener(
          'message',
          (e) => {
            clearTimeout(timeout);
            resolve(e);
          },
          { once: true },
        );
      });

      this.logger_.debug(`Fetching license for key ${keyId}`);

      // Fetch license from server
      const response = await this.getResponse(message, this.fpLicenseUrl);

      this.logger_.debug(`License received for key ${keyId}, updating session`);

      // Update session with license
      await session.update(response);

      // Don't wait for key to become usable - let playback proceed naturally
      // The browser will use the key when ready
      this.logger_.info(`FairPlay license applied for key ${keyId}`);

      // Start a timeout to detect if keys never become usable
      // This can happen due to HDCP/output restrictions on some platforms
      this._startKeyUsableTimeout();
    } catch (error) {
      this.logger_.error(
        `FairPlay processing failed for key ${keyId}:`,
        error,
      );
      // Clean up
      this.sessions.delete(keyId);
      throw error;
    }
  }

  /**
   * Handle key status changes
   * @param {MediaKeySession} session
   * @param {string} keyId
   */
  _onKeyStatusChange(session, keyId) {
    session.keyStatuses.forEach((status, _key) => {
      this.logger_.debug(`Key ${keyId} status: ${status}`);

      switch (status) {
        case 'usable':
          this.logger_.info(`Key ${keyId} is now usable`);
          this.usableKeys.add(keyId);
          if (this._keyUsableTimeout) {
            clearTimeout(this._keyUsableTimeout);
            this._keyUsableTimeout = null;
          }
          break;

        case 'expired':
          this.logger_.warn(`Key ${keyId} expired`);
          this._dispatchError(
            'keySystemExpired',
            'The content license has expired.',
          );
          this.sessions.delete(keyId);
          session.close().catch(() => {});
          break;

        case 'internal-error':
          this.logger_.error(`Key ${keyId} internal error`);
          this._dispatchError(
            'keySystemStatusInternalError',
            'There was an error with the content protection system.',
          );
          this.sessions.delete(keyId);
          session.close().catch(() => {});
          break;

        case 'output-restricted':
          this.logger_.warn(`Key ${keyId} output restricted`);
          this._dispatchError(
            'keySystemStatusOutputRestricted',
            "This video cannot be played on your device because it doesn't meet the content protection requirements.",
          );
          break;

        case 'output-downscaled':
          this.logger_.warn(`Key ${keyId} output downscaled`);
          break;
      }
    });
  }

  /**
   * Dispatch an error to the player
   * @param {string} code - Error code
   * @param {string} message - Human-readable error message
   */
  _dispatchError(code, message) {
    if (this.onError) {
      this.onError(code, message);
    }
  }

  /**
   * Start a timeout to detect if keys never become usable
   * This can happen due to HDCP/output restrictions that aren't explicitly reported
   */
  _startKeyUsableTimeout() {
    // Only start once
    if (this._keyUsableTimeout) {
      return;
    }

    // Wait 10 seconds for at least one key to become usable
    this._keyUsableTimeout = setTimeout(() => {
      if (this.usableKeys.size === 0 && this.sessions.size > 0) {
        this.logger_.error('DRM keys failed to become usable');
        this._dispatchError(
          'keySystemInitError',
          'There was an error during initialization of the DRM system.',
        );
      }
    }, 10000);
  }

  /**
   * Fetch license from license server
   * @param {MediaKeyMessageEvent} messageEvent
   * @param {string} url
   * @returns {Promise<ArrayBuffer>}
   */
  async getResponse(messageEvent, url) {
    this.logger_.debug('Fetching license from:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      body: messageEvent.message,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'unknown');
      throw new Error(
        `License fetch failed: ${response.status} - ${errorText}`,
      );
    }

    const ckc = await response.arrayBuffer();
    if (ckc.byteLength === 0) {
      throw new Error('License server returned empty response');
    }

    return ckc;
  }

  /**
   * Clean up all sessions and event listeners
   */
  cleanup() {
    this.logger_.debug('Cleaning up DRM engine');

    // Clear timeout
    if (this._keyUsableTimeout) {
      clearTimeout(this._keyUsableTimeout);
      this._keyUsableTimeout = null;
    }

    // Remove event listener
    if (this._videoElement && this._boundOnEncrypted) {
      this._videoElement.removeEventListener(
        'encrypted',
        this._boundOnEncrypted,
      );
    }

    // Close all sessions
    for (const [keyId, session] of this.sessions) {
      this.logger_.debug(`Closing session for key ${keyId}`);
      session.close().catch(() => {});
    }

    // Reset state
    this.sessions.clear();
    this.pendingRequests.clear();
    this.usableKeys.clear();
    this.mediaKeysPromise = null;
    this.mediaKeysReady = false;
    this._videoElement = null;
    this._boundOnEncrypted = null;
  }

  /**
   * Get the number of active sessions
   * @returns {number}
   */
  get sessionCount() {
    return this.sessions.size;
  }
}
